import React from "react";
import { Link } from "react-router-dom";
import "./LandingPage.css";

// Configuration - Update these values
const CONFIG = {
  calendlyUrl: "https://calendly.com/routebot/intro", // Replace with your Calendly link
  companyName: "RouteBot",
  email: "hello@routebot.com",
};

const LandingPage = () => {
  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <a href="/" className="landing-logo">
          <span className="landing-logo-icon">🚌</span>
          {CONFIG.companyName}
        </a>
        <div className="landing-nav-links">
          <a href="#features" className="landing-nav-link">
            Features
          </a>
          <a href="#how-it-works" className="landing-nav-link">
            How It Works
          </a>
          <a href="#pricing" className="landing-nav-link">
            Pricing
          </a>
          <Link to="/login" className="landing-nav-link">
            Login
          </Link>
          <a href={CONFIG.calendlyUrl} target="_blank" rel="noopener noreferrer" className="landing-nav-cta">
            Book Demo
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <h1>We Set Up Your Transportation System in 48 Hours</h1>
        <p className="landing-hero-subtitle">
          Stop struggling with spreadsheets. We'll configure your entire school bus or employee shuttle management
          system — routes, drivers, notifications — so you can focus on what matters.
        </p>
        <a href={CONFIG.calendlyUrl} target="_blank" rel="noopener noreferrer" className="landing-hero-cta">
          Book a Free Demo
          <span className="landing-hero-cta-arrow">→</span>
        </a>
        <p className="landing-hero-trust">Trusted by transportation companies managing 1M+ monthly trips</p>
      </section>

      {/* Features Section */}
      <section className="landing-features" id="features">
        <div className="landing-features-container">
          <h2>Everything You Need to Manage Transportation</h2>
          <p className="landing-features-subtitle">One platform for routes, drivers, parents, and real-time tracking</p>

          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🗺️</div>
              <h3>Smart Route Optimization</h3>
              <p>
                AI-powered routing that automatically groups students by location and optimizes pickup sequences to save
                time and fuel.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📱</div>
              <h3>Mobile App for Parents</h3>
              <p>
                Parents track their child's bus in real-time and receive automatic notifications when the bus is
                approaching.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">🚐</div>
              <h3>Driver Management</h3>
              <p>Assign drivers to routes, track their location, and manage their schedules all from one dashboard.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📊</div>
              <h3>Attendance Tracking</h3>
              <p>Know exactly who got on and off the bus. Automatic alerts for no-shows and unexpected absences.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">💬</div>
              <h3>SMS Notifications</h3>
              <p>Keep parents informed with automated SMS alerts for delays, arrivals, and schedule changes.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">📈</div>
              <h3>Reports & Analytics</h3>
              <p>Generate detailed reports on routes, fuel consumption, driver performance, and ridership trends.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="landing-how-it-works" id="how-it-works">
        <div className="landing-how-it-works-container">
          <h2>How It Works</h2>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Book a Demo Call</h3>
                <p>
                  Schedule a free 30-minute call where we'll understand your needs and show you exactly how RouteBot
                  works for your situation.
                </p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Send Us Your Data</h3>
                <p>
                  Share your student/employee list, vehicle information, and locations. A simple Excel file is all we
                  need.
                </p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>We Set Everything Up</h3>
                <p>
                  Within 48 hours, we configure your entire system — routes, drivers, stops, and notifications. You
                  don't lift a finger.
                </p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">4</div>
              <div className="landing-step-content">
                <h3>Go Live</h3>
                <p>
                  We hand over the keys to your fully configured system. We'll do a walkthrough call to make sure you're
                  comfortable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="landing-pricing" id="pricing">
        <div className="landing-pricing-container">
          <h2>Pricing</h2>
          <p className="landing-pricing-subtitle">
            Pricing is based on the number of routes. Start free with up to 10 routes. Paid plans start at $120/month
            and scale with your operation. The Free plan is a permanent $0 plan, not a trial.
          </p>

          <div className="landing-pricing-card">
            <div className="landing-pricing-features">
              <div className="landing-pricing-feature">
                <span>Free — $0/month — up to 10 routes</span>
              </div>
              <div className="landing-pricing-feature">
                <span>Starter — $120/month — up to 40 routes</span>
              </div>
              <div className="landing-pricing-feature">
                <span>Pro — $500/month — up to 200 routes</span>
              </div>
              <div className="landing-pricing-feature">
                <span>Business — $1,500/month — up to 750 routes</span>
              </div>
              <div className="landing-pricing-feature">
                <span>More than 750 routes — Custom pricing</span>
              </div>
            </div>

            <a href="https://routebot.com/pricing" className="landing-pricing-cta">
              View pricing
            </a>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="landing-testimonial">
        <div className="landing-testimonial-container">
          <p className="landing-testimonial-quote">
            "We were managing 50 school buses with spreadsheets and WhatsApp groups. RouteBot transformed our
            operations. Parents love the real-time tracking, and we've reduced late arrivals by 40%."
          </p>
          <p className="landing-testimonial-author">Transportation Manager</p>
          <p className="landing-testimonial-role">Private School, Turkey</p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="landing-cta-section">
        <div className="landing-cta-container">
          <h2>Ready to Simplify Your Transportation?</h2>
          <p className="landing-cta-subtitle">Book a free demo call. No commitment, no credit card required.</p>
          <a href={CONFIG.calendlyUrl} target="_blank" rel="noopener noreferrer" className="landing-cta-button">
            Schedule Your Free Demo
            <span>→</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-container">
          <div className="landing-footer-links">
            <Link to="/privacy" className="landing-footer-link">
              Privacy Policy
            </Link>
            <Link to="/terms" className="landing-footer-link">
              Terms of Service
            </Link>
            <a href={`mailto:${CONFIG.email}`} className="landing-footer-link">
              Contact
            </a>
          </div>
          <p className="landing-footer-copyright">
            © {new Date().getFullYear()} {CONFIG.companyName}. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
